import { useState, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'

// Auth Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'

// Main Game Pages
import Dashboard from './pages/Dashboard'
import FleetManagement from './pages/FleetManagement'
import GalaxyMap from './pages/GalaxyMap'
import ResearchCenter from './pages/ResearchCenter'
import Diplomacy from './pages/Diplomacy'
import Combat from './pages/Combat'
import Rankings from './pages/Rankings'
import Intelligence from './pages/Intelligence'
import Community from './pages/Community'

// Management Pages
import Market from './pages/Market'
import Alliances from './pages/Alliances'

// User Pages
import Settings from './pages/Settings'

// Admin Pages
import AdminDashboard from './pages/AdminDashboard'
import Support from './pages/Support'
import About from './pages/About'
import NotFound from './pages/NotFound'

// Layout Components
import Starfield from './components/Starfield'
import Navigation from './components/Navigation'
import Sidebar from './components/Sidebar'
import { auth, db } from './services/supabase'

// Types
interface User {
  id: string
  name: string
  email: string
  level: number
  faction?: string
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const location = useLocation()

  // Check authentication status on app load and listen for changes
  useEffect(() => {
    const checkAuth = async () => {
      const { user: supabaseUser } = await auth.getCurrentUser()
      
      if (supabaseUser) {
        // Get user profile from database
        const profileResult = await db.getUserProfile(supabaseUser.id)
        
        let userName = 'Space Commander'
        let userLevel = 1
        let userFaction = 'Terran Empire'

        if (profileResult.data) {
          userName = profileResult.data.username
          userLevel = profileResult.data.level
          userFaction = profileResult.data.civilization_name || 'Terran Empire'
        }

        const userData: User = {
          id: supabaseUser.id,
          name: userName,
          email: supabaseUser.email || '',
          level: userLevel,
          faction: userFaction
        }

        setUser(userData)
        setIsAuthenticated(true)
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
      
      setIsLoading(false)
    }

    checkAuth()

    // Listen for auth state changes
    const { data: { subscription } } = auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        // User signed in
        const profileResult = await db.getUserProfile(session.user.id)
        
        let userName = 'Space Commander'
        let userLevel = 1
        let userFaction = 'Terran Empire'

        if (profileResult.data) {
          userName = profileResult.data.username
          userLevel = profileResult.data.level
          userFaction = profileResult.data.civilization_name || 'Terran Empire'
        }

        const userData: User = {
          id: session.user.id,
          name: userName,
          email: session.user.email || '',
          level: userLevel,
          faction: userFaction
        }

        setUser(userData)
        setIsAuthenticated(true)
      } else {
        // User signed out
        setUser(null)
        setIsAuthenticated(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleLogin = (userData: User) => {
    setUser(userData)
    setIsAuthenticated(true)
    localStorage.setItem('crown_of_suns_user', JSON.stringify(userData))
  }

  const handleLogout = async () => {
    await auth.signOut()
    // Supabase auth state change will handle clearing user state
  }

  // Show loading screen during auth check
  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Starfield />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Crown of Suns
            </div>
            <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto"></div>
            <div className="text-gray-400">Initializing galaxy...</div>
          </div>
        </div>
      </div>
    )
  }

  // Public routes (no auth required)
  const isPublicRoute = ['/', '/login', '/register', '/accept-invite'].includes(location.pathname)

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Starfield />
      
      <div className="relative z-10">
        {/* Sidebar - only show on authenticated routes */}
        {isAuthenticated && !isPublicRoute && (
          <Sidebar 
            user={user} 
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        )}

        {/* Top Navigation - only show on authenticated routes */}
        {isAuthenticated && !isPublicRoute && (
          <Navigation user={user} onLogout={handleLogout} />
        )}

        {/* Main Content */}
        <div className={`${isAuthenticated && !isPublicRoute ? `pt-16 ${sidebarCollapsed ? 'pl-16' : 'pl-64'}` : ''} transition-all duration-300`}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={user ? <Dashboard user={user} /> : <LandingPage />} />
            
            {/* Core Game Pages */}
            <Route path="/fleet" element={<FleetManagement />} />
            <Route path="/galaxy" element={<GalaxyMap />} />
            <Route path="/research" element={<ResearchCenter />} />
            <Route path="/diplomacy" element={<Diplomacy />} />
            <Route path="/combat" element={<Combat />} />
            <Route path="/rankings" element={<Rankings />} />
            <Route path="/intelligence" element={<Intelligence />} />
            <Route path="/market" element={<Market />} />
            <Route path="/alliances" element={<Alliances />} />
            <Route path="/community" element={<Community />} />

            {/* User Pages */}
            <Route path="/settings" element={<Settings />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/support" element={<Support />} />
            <Route path="/about" element={<About />} />

            {/* Fallback Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

export default App